import { useState, useEffect, useCallback } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router'
import { authService } from '@/services/auth-service'
import { useLogger } from '@/hooks/useLogger'
import { useTokenRefresh } from '@/hooks/useTokenRefresh'
import { SessionExpiredModal } from '@/components/system/SessionExpiredModal'
import { SessionExpiryWarning } from '@/components/system/SessionExpiryWarning'
import { clearLocalStorage as clearWindowState } from '@/services/window-state-service'
import { rememberPostLoginTarget } from '@/lib/post-login-redirect'
import { RouteLoading } from './RouteLoading'
import { SettingsProvider } from '@/contexts/SettingsContext'

export function ProtectedRoute() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [showSessionExpired, setShowSessionExpired] = useState(false)
  const [showExpiryWarning, setShowExpiryWarning] = useState(false)
  const logger = useLogger('ProtectedRoute')
  const navigate = useNavigate()
  const location = useLocation()

  // Aktiviere automatischen Token-Refresh für eingeloggte User
  useTokenRefresh()

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Nur noch die Anmeldung. Welche Anwendung eine Route voraussetzt,
        // entscheidet `RequireApplication` je Route (#504) — pauschal `webos`
        // zu verlangen, sperrte reine PDB-Nutzer aus /chat aus.
        const authStatus = await authService.isAuthenticated()
        setIsAuthenticated(authStatus)
        if (!authStatus) {
          logger.info('User not authenticated, redirecting to login.')
        }
      } catch (error) {
        logger.error({ err: error }, 'Authentication check failed.')
        setIsAuthenticated(false)
      }
    }
    checkAuth()
  }, [logger])

  const handleRelogin = useCallback(async () => {
    // Clear MSAL cache so login page shows sign-in button (app settings preserved).
    // Await it so the navigate happens only after MSAL has actually flushed
    // its cache — otherwise the login page can briefly see the stale account.
    await authService.clearForRelogin()
    navigate('/', { replace: true })
  }, [navigate])

  // Deep-Link über den Login retten (#504): wer abgemeldet `/chat` aufruft, soll
  // nach der Anmeldung dort landen — samt Hash, dem Desktop-Deep-Link (#1135).
  useEffect(() => {
    if (isAuthenticated === false) {
      rememberPostLoginTarget(`${location.pathname}${location.search}${location.hash}`)
    }
  }, [isAuthenticated, location.pathname, location.search, location.hash])

  // Listen for session lifecycle events
  useEffect(() => {
    const handleSessionExpiring = () => {
      logger.warn('Auth session expiring soon - showing warning')
      setShowExpiryWarning(true)
    }

    const handleSessionExtended = () => {
      logger.info('Auth session extended - hiding warning')
      setShowExpiryWarning(false)
    }

    const handleSessionExpired = () => {
      logger.warn('Auth session expired - invalidating token and showing re-login modal')
      authService.clearLocalSession()
      // Clear window state so expired windows are not restored after re-login
      clearWindowState()
      setShowExpiryWarning(false)
      setShowSessionExpired(true)
      // We deliberately leave `isAuthenticated` true so the modal continues
      // to render (the modal lives below the `<Navigate>` short-circuit).
      // Background polling hooks may still fire 401s until the user clicks
      // "Sign in again"; the 401-retry helper in cdb-tqs-api-service makes
      // those a no-op on the second attempt (refresh fails → silent skip).
    }

    window.addEventListener('auth-session-expiring', handleSessionExpiring)
    window.addEventListener('auth-session-extended', handleSessionExtended)
    window.addEventListener('auth-session-expired', handleSessionExpired)

    return () => {
      window.removeEventListener('auth-session-expiring', handleSessionExpiring)
      window.removeEventListener('auth-session-extended', handleSessionExtended)
      window.removeEventListener('auth-session-expired', handleSessionExpired)
    }
  }, [logger])

  // Erstes von zwei Gates: dieser Zustand ist das Allererste, was nach der
  // Anmeldung erscheint. Begruendung in `RouteLoading` (#699).
  if (isAuthenticated === null) {
    return <RouteLoading />
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />
  }

  // Settings erst hinter der Anmeldung laden: auf der Login-Seite (auch
  // `/login` über den Catch-all) gibt es niemanden, dessen Settings es gäbe —
  // ein Load dort lieferte früher Standardwerte, die später über die echten
  // geschrieben wurden (#908).
  return (
    <SettingsProvider>
      <Outlet />
      <SessionExpiryWarning
        open={showExpiryWarning}
        onDismiss={() => setShowExpiryWarning(false)}
      />
      <SessionExpiredModal open={showSessionExpired} onRelogin={handleRelogin} />
    </SettingsProvider>
  )
}
